import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import React, { useState } from "react";
import { Line } from "react-chartjs-2";
import { Layout, styles } from "../../../layout";

// Register Chart.js
ChartJS.register(
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
  Legend
);

const HowMuchHaveIMade: React.FC = () => {
  const [ticker, setTicker] = useState("");
  const [date, setDate] = useState("");
  const [amount, setAmount] = useState<number>(0);
  const [reinvestDividends, setReinvestDividends] = useState(false);

  const [labels, setLabels] = useState<string[]>([]);
  const [portfolioValues, setPortfolioValues] = useState<number[]>([]);
  const [shares, setShares] = useState<number>(0);
  const [finalValue, setFinalValue] = useState<number>(0);
  const [cagr, setCagr] = useState<number>(0);
  const [yearlyData, setYearlyData] = useState<
    { year: string; value: number; pct: number; dividends: number }[]
  >([]);

  const fetchData = async () => {
    if (!ticker || !date || !amount) return;

    try {
      const url = `/.netlify/functions/stock?ticker=${ticker}&date=${date}`;
      const res = await fetch(url);
      const data = await res.json();

      if (data.error) {
        alert(data.error);
        return;
      }

      const dates = data.data.map((d: any) => d.date);
      const prices = data.data.map((d: any) => d.close);
      const dividends = data.dividends || [];

      // Initial purchase
      const purchasePrice = prices[0];
      let numShares = amount / purchasePrice;

      // Track dividends received by year (cash, not reinvested)
      const dividendsByYear: Record<string, number> = {};

      // Portfolio values
      const values: number[] = [];
      dates.forEach((d: string, idx: number) => {
        // If there’s a dividend on this date
        const div = dividends.find((dv: any) => dv.date === d);
        if (div) {
          const dividendCash = numShares * div.amount;
          const year = d.split("-")[0];
          dividendsByYear[year] = (dividendsByYear[year] || 0) + dividendCash;

          if (reinvestDividends) {
            const extraShares = dividendCash / prices[idx];
            numShares += extraShares;
          }
        }
        values.push(prices[idx] * numShares);
      });

      setLabels(dates);
      setPortfolioValues(values);
      setShares(numShares);
      setFinalValue(values[values.length - 1]);

      // CAGR calculation
      const startDate = new Date(date).getTime();
      const endDate = new Date().getTime();
      const years = (endDate - startDate) / (1000 * 60 * 60 * 24 * 365);

      if (years > 0) {
        const cagrVal =
          Math.pow(values[values.length - 1] / amount, 1 / years) - 1;
        setCagr(cagrVal);
      }

      // Calendarized yearly breakdown
      const yearEnd: Record<string, number> = {};
      dates.forEach((d: string, idx: number) => {
        const year = d.split("-")[0];
        yearEnd[year] = values[idx]; // last value of that year
      });

      const tableData = Object.keys(yearEnd).map((year) => ({
        year,
        value: yearEnd[year],
        pct: ((yearEnd[year] - amount) / amount) * 100,
        dividends: dividendsByYear[year] || 0, // ✅ force default to 0
      }));

      setYearlyData(tableData);
    } catch (err) {
      console.error("Error fetching data:", err);
    }
  };

  const chartData = {
    labels,
    datasets: [
      {
        label: `Value of Investment in ${ticker.toUpperCase()}`,
        data: portfolioValues,
        borderColor: "#217E38",
        backgroundColor: "rgba(33, 126, 56, 0.2)",
        fill: true,
        tension: 0.3,
      },
    ],
  };

  return (
    <Layout title="How Much Have I Made?">
      <p style={styles.paragraph}>
        Enter a ticker, purchase date, and investment amount to see how much
        your shares would be worth today.
      </p>

      <div style={custom.inputGroup}>
        <label style={custom.label}>Ticker</label>
        <input
          type="text"
          value={ticker}
          onChange={(e) => setTicker(e.target.value)}
          style={custom.input}
          placeholder="e.g. AAPL"
        />
      </div>

      <div style={custom.inputGroup}>
        <label style={custom.label}>Purchase Date</label>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          style={custom.input}
        />
      </div>

      <div style={custom.inputGroup}>
        <label style={custom.label}>Amount Invested ($)</label>
        <input
          type="number"
          value={amount}
          onChange={(e) => setAmount(Number(e.target.value))}
          style={custom.input}
          placeholder="e.g. 1000"
        />
      </div>

      <div style={custom.inputGroup}>
        <label style={custom.label}>
          <input
            type="checkbox"
            checked={reinvestDividends}
            onChange={(e) => setReinvestDividends(e.target.checked)}
          />
          Reinvest Dividends
        </label>
      </div>

      <button onClick={fetchData} style={custom.button}>
        Calculate
      </button>

      {portfolioValues.length > 0 && (
        <div style={{ marginTop: "30px" }}>
          <Line data={chartData} />

          <p style={styles.paragraph}>
            You bought <b>{shares.toFixed(2)}</b> shares of{" "}
            {ticker.toUpperCase()} on {date}.
          </p>
          <p style={styles.paragraph}>
            Initial investment: <b>${amount.toFixed(2)}</b>
          </p>
          <p style={styles.paragraph}>
            Current value: <b>${finalValue.toFixed(2)}</b>
          </p>
          <p style={styles.paragraph}>
            Total Return:{" "}
            <b>{(((finalValue - amount) / amount) * 100).toFixed(2)}%</b>
          </p>
          <p style={styles.paragraph}>
            Annualized Return (CAGR): <b>{(cagr * 100).toFixed(2)}%</b>
          </p>

          <h3 style={styles.subHeader}>Yearly Breakdown</h3>
          <table style={custom.table}>
            <thead>
              <tr>
                <th>Year</th>
                <th>Portfolio Value ($)</th>
                <th>Total Return (%)</th>
                <th>Dividends ($)</th>
              </tr>
            </thead>
            <tbody>
              {yearlyData.map((row, idx) => (
                <tr key={idx}>
                  <td>{row.year}</td>
                  <td>${row.value.toFixed(2)}</td>
                  <td>{row.pct.toFixed(2)}%</td>
                  <td>${(row.dividends || 0).toFixed(2)}</td> {/* ✅ safe */}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Layout>
  );
};

const custom = {
  inputGroup: {
    marginBottom: "20px",
    display: "flex",
    flexDirection: "column" as "column",
    maxWidth: "300px",
  },
  input: {
    padding: "8px",
    border: "1px solid #a30000ff",
    borderRadius: "4px",
    fontSize: "16px",
  },
  label: {
    marginBottom: "5px",
    fontWeight: "bold",
  },
  button: {
    marginTop: "10px",
    padding: "8px 12px",
    border: "none",
    backgroundColor: "#217E38",
    color: "#fff",
    borderRadius: "4px",
    cursor: "pointer",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse" as "collapse",
    marginTop: "20px",
  },
  th: {
    border: "1px solid #ccc",
    padding: "8px",
  },
  td: {
    border: "1px solid #ccc",
    padding: "8px",
  },
};

export default HowMuchHaveIMade;
